import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import api from "../../lib/api";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", text: "Welcome to 220 Inc. I'm your AI Concierge — ask me about subsidiaries, veteran housing, investor opportunities, or how to get involved." },
  ]);
  const sessionId = useRef(`sess_${Math.random().toString(36).slice(2)}`);
  const msgCounter = useRef(0);
  const endRef = useRef(null);

  const pushMessage = (role, text) => {
    msgCounter.current += 1;
    setMessages((m) => [...m, { id: `m${msgCounter.current}`, role, text }]);
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    pushMessage("user", text);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text });
      pushMessage("assistant", data.reply);
    } catch (e) {
      pushMessage("assistant", "I had trouble responding. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className="fixed bottom-6 left-6 z-[9999] btn-primary !rounded-full !p-4 shadow-lg shadow-black/50"
        onClick={() => setOpen(true)}
        aria-label="Open AI concierge"
        data-testid="chatbot-launcher"
      >
        <MessageSquare size={22} />
      </button>

      {open && (
        <div className="fixed bottom-6 left-6 z-[9999] w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-3rem))] flex flex-col glass border border-white/10" data-testid="chatbot-panel">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gold">Concierge</div>
              <div className="text-sm text-white/80">220 Inc. AI · Claude Sonnet 4.5</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="chatbot-close"><X size={20} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {messages.map((m) => (
              <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex"}>
                <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-gold text-black"
                    : "bg-white/5 text-white/90 border border-white/10"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-white/60 text-sm"><Loader2 className="animate-spin" size={14}/> Thinking…</div>
            )}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about subsidiaries, housing, investors…"
              className="flex-1 bg-[#0f0f10] border border-white/10 px-3 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold"
              data-testid="chatbot-input"
            />
            <button onClick={send} disabled={busy} className="btn-primary !py-3 !px-4" data-testid="chatbot-send">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
